<template>
  <div class="alice-saved">
    <SaveNoteDialog
      :dialog="saveDialog"
      :note="editingNote"
      :existing-labels="labels"
      @close="closeSaveDialog"
      @save="onSaveNote"
    />

    <!-- ============== Header ============== -->
    <header class="alice-saved__head">
      <div class="alice-saved__head-main">
        <div class="alice-saved__crumb">
          <span>Project</span>
          <Icon name="chevron-right" :size="12" />
          <span>Notes</span>
        </div>
        <h1 class="alice-saved__title">
          Notes
          <span v-if="savedNotes.length" class="alice-saved__count">{{
            savedNotes.length
          }}</span>
        </h1>
        <p class="alice-saved__subtitle">
          Pareltjes die je uit de chat hebt bewaard. Geef ze een label zodat je
          later weet waar ze bij hoorden — en haal ze terug in de chat wanneer
          je er verder mee wilt.
        </p>
      </div>
    </header>

    <div v-if="!project" class="alice-saved__empty">Loading project…</div>

    <template v-else>
      <!-- ============== Controls ============== -->
      <div v-if="savedNotes.length" class="alice-saved__controls">
        <label class="alice-saved__search">
          <Icon name="search" :size="16" />
          <input v-model="query" type="text" placeholder="Zoek in notities…" />
        </label>
        <div v-if="labels.length" class="alice-saved__labels">
          <button
            class="alice-saved__label-chip"
            :class="{ 'alice-saved__label-chip--active': activeLabel === null }"
            type="button"
            @click="activeLabel = null"
          >
            Alle
          </button>
          <button
            v-for="l in labels"
            :key="l"
            class="alice-saved__label-chip"
            :class="{ 'alice-saved__label-chip--active': activeLabel === l }"
            type="button"
            @click="activeLabel = activeLabel === l ? null : l"
          >
            {{ l }}
          </button>
        </div>
      </div>

      <!-- ============== Grouped list ============== -->
      <div v-if="grouped.length" class="alice-saved__groups">
        <section
          v-for="group in grouped"
          :key="group.label"
          class="alice-saved__group"
        >
          <h2 class="alice-saved__group-title">
            {{ group.label || "Geen label" }}
            <span class="alice-saved__group-count">{{
              group.notes.length
            }}</span>
          </h2>
          <SavedNotesList
            :notes="group.notes"
            @bring-to-chat="bringSavedToChat"
            @edit="editSavedNote"
            @delete="deleteSaved"
          />
        </section>
      </div>

      <div v-else class="alice-saved__empty">
        <template v-if="savedNotes.length">
          Geen notities gevonden voor deze filter.
        </template>
        <template v-else>
          Nog niets bewaard. Open de <strong>Chat</strong>, selecteer tekst in
          een antwoord (of bewaar het hele antwoord) met de
          <strong>Bewaar</strong>-knop.
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState } from "vuex";
import Icon from "@/components/alice/Icon.vue";
import SaveNoteDialog from "@/components/SaveNoteDialog.vue";
import SavedNotesList from "@/components/SavedNotesList.vue";

export default {
  name: "ProjectSaved",
  components: { Icon, SaveNoteDialog, SavedNotesList },
  data() {
    return {
      query: "",
      activeLabel: null,
      saveDialog: false,
      editingNote: null,
    };
  },
  computed: {
    ...mapState({
      project: (state) => state.project,
    }),
    savedNotes() {
      return this.project?.saved || [];
    },
    labels() {
      const set = new Set();
      this.savedNotes.forEach((n) => {
        if (n.label) set.add(n.label);
      });
      return [...set].sort((a, b) => a.localeCompare(b));
    },
    filtered() {
      const q = this.query.trim().toLowerCase();
      return this.savedNotes.filter((n) => {
        if (this.activeLabel !== null && (n.label || "") !== this.activeLabel)
          return false;
        if (!q) return true;
        return (
          (n.text || "").toLowerCase().includes(q) ||
          (n.label || "").toLowerCase().includes(q) ||
          (n.note || "").toLowerCase().includes(q)
        );
      });
    },
    grouped() {
      const map = new Map();
      this.filtered.forEach((n) => {
        const key = n.label || "";
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(n);
      });
      // Labelled groups first (alphabetical), unlabelled last.
      return [...map.entries()]
        .sort((a, b) => {
          if (a[0] === "") return 1;
          if (b[0] === "") return -1;
          return a[0].localeCompare(b[0]);
        })
        .map(([label, notes]) => ({ label, notes }));
    },
  },
  methods: {
    editSavedNote(note) {
      this.editingNote = note;
      this.saveDialog = true;
    },
    closeSaveDialog() {
      this.saveDialog = false;
      this.editingNote = null;
    },
    async onSaveNote(payload) {
      await this.$store.dispatch("updateSavedNote", payload);
      this.closeSaveDialog();
    },
    deleteSaved(note) {
      this.$store.dispatch("deleteSavedNote", note.uid);
    },
    bringSavedToChat(note) {
      this.$store.commit("setChatPrefill", note.text || "");
      const id = this.project?.uid || this.$route.params.id;
      this.$router.push(`/project/${id}/chat`);
    },
  },
};
</script>

<style lang="scss" scoped>
.alice-saved {
  // The app shell (.alice-shell__main) is fixed-height with overflow:hidden,
  // so this view must own its scroll instead of relying on the page to scroll.
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 32px 40px 64px;
  max-width: 900px;

  &__crumb {
    display: flex;
    align-items: center;
    gap: 6px;
    font-family: var(--font-ui);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--fg-3);
    margin-bottom: 12px;
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0;
    font-family: var(--font-display);
    font-size: 36px;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1;
    color: var(--vp-black);
  }

  &__count {
    font-family: var(--font-mono);
    font-size: 16px;
    font-weight: 700;
    color: var(--fg-3);
  }

  &__subtitle {
    margin: 12px 0 0;
    max-width: 640px;
    font-family: var(--font-body);
    font-size: 15px;
    line-height: 1.5;
    color: var(--fg-2);
  }

  &__controls {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 28px 0 24px;
  }

  &__search {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 360px;
    padding: 8px 12px;
    border: 1px solid var(--border-hairline);
    background: var(--vp-white);
    color: var(--fg-3);

    input {
      border: none;
      outline: none;
      background: transparent;
      font-family: var(--font-body);
      font-size: 14px;
      color: var(--vp-black);
      width: 100%;
    }
  }

  &__labels {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__label-chip {
    padding: 5px 12px;
    background: var(--vp-white);
    border: 1px solid var(--border-hairline);
    cursor: pointer;
    font-family: var(--font-ui);
    font-size: 12px;
    font-weight: 600;
    color: var(--fg-2);
    transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;

    &:hover {
      border-color: var(--vp-black);
      color: var(--vp-black);
    }

    &--active,
    &--active:hover {
      background: var(--vp-black);
      border-color: var(--vp-black);
      color: var(--vp-white);
      font-weight: 700;
    }
  }

  &__groups {
    display: flex;
    flex-direction: column;
    gap: 32px;
  }

  &__group-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 12px;
    font-family: var(--font-ui);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--fg-2);
  }

  &__group-count {
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--fg-3);
  }

  &__empty {
    margin-top: 32px;
    padding: 24px;
    border: 1px dashed var(--border-hairline);
    font-family: var(--font-body);
    font-size: 15px;
    line-height: 1.5;
    color: var(--fg-2);
  }
}
</style>
